import apiService from "@/api-service";
import BestekFilter from "@/components/bestek-filter/bestek-filter";
import { BestekFilterParams, mapFilterParamsToQueryParams, initialBestekFilterParams as defaultBestekFilterParams } from "@/components/bestek-filter/bestek-filter-params";
import HeaderedContainer from "@/components/headered-container/headered-container";
import { RootState } from "@/store";
import { min } from "date-fns";
import { useEffect, useState } from "react";
import { Button, ButtonGroup, Col, Container, Form, Row, Spinner, Table } from "react-bootstrap";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { IconContext } from "react-icons";
import { MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styles from "./list.module.scss";

interface WordCount {
    word: string;
    length: number;
    count: number;
    bestek_ids: number[];
    total_texts: number;
    texts: string[];
}

interface Props {
    
}

function mapStateToProps(state: RootState): Props {
    return {
        
    };
}

const WordCountList = (props: Props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [ bestekFilterParamsQuery, setBestekFilterParamsQuery ] = useState<any>({});
    const [ bestekFilterParamsExpanded, setBestekFilterParamsExpanded ] = useState<boolean>(true);
    const [ initialBestekFilterParams, setInitialBestekFilterParams ] = useState<BestekFilterParams>();
    const [ bestekFilterParams, setBestekFilterParams ] = useState<BestekFilterParams>(defaultBestekFilterParams);
    const [ shouldAddMoreFilterCriteria, setShouldAddMoreFilterCriteria ] = useState<boolean>(true);

    const [ result, setResult ] = useState<WordCount[]>([]);
    const [ loading, setLoading ] = useState<boolean>(false);

    const [ minLength, setMinLength ] = useState<number>(10);
    const [ maxLength, setMaxLength ] = useState<number>(40);
    const [ minCount, setMinCount ] = useState<number>(25);
    const [ minBestekCount, setMinBestekCount ] = useState<number>(25);

    const [ fetchTimeout, setFetchTimeout ] = useState<any>();
    const [ doFetch, setDoFetch ] = useState<boolean>(false);

    const [ showResultForLength, setShowResultForLength ] = useState<number>(0);
    const [ longestSubsentenceLength, setLongestSubsentenceLength ] = useState<number>(0);
    const [ availableLengths, setAvailableLengths ] = useState<number[]>([]);

    useEffect(() => {
        if (doFetch) {
            fetch();
        }
    });

    useEffect(() => {
        fetchWithTimeout();
    }, [minLength, minCount, minBestekCount]);

    const fetch = (): void => {
        if (Object.keys(bestekFilterParamsQuery).length < 1) {
            setResult([]);
            setShouldAddMoreFilterCriteria(true);
            return;
        }

        setShouldAddMoreFilterCriteria(false);

        setLoading(true);
        setDoFetch(false);
        apiService.post<{ result: WordCount[], longest_subsentence_length: number, available_lengths: number[] }>('/stats/word-count', {
            bestek_filter: bestekFilterParamsQuery,
            min_length: minLength,
            // max_length: maxLength,
            min_count: minCount,
            min_bestek_count: minBestekCount,
            only_99: true
        }).then(
            (response) => {
                setResult(response.data.result);
                setLongestSubsentenceLength(response.data.longest_subsentence_length);
                setShowResultForLength(response.data.longest_subsentence_length);
                setAvailableLengths(response.data.available_lengths);
            }, (e) => {
                setResult([]);
                setShouldAddMoreFilterCriteria(true);
            }
        ).finally(() => setLoading(false));
    }

    const pageSize: number = 10;
    const [ activePage, setActivePage ] = useState<number>(1);

    const filteredResultSet = result.filter(r => r.length === showResultForLength);

    if (filteredResultSet.length < pageSize * (activePage - 1)) {
        setActivePage(1);
    }
    const startIndex = (activePage - 1) * pageSize;
    const shownResultSet = filteredResultSet.slice(startIndex, Math.min(filteredResultSet.length, startIndex + pageSize));

    const bestekFilterCollapseIcon = (): JSX.Element => {
        if (bestekFilterParamsExpanded) {
            return <IconContext.Provider value={{ size: '25px' }}><MdKeyboardArrowUp onClick={() => setBestekFilterParamsExpanded(false)} /></IconContext.Provider>
        } else {
            return <IconContext.Provider value={{ size: '25px' }}><MdKeyboardArrowDown onClick={() => setBestekFilterParamsExpanded(true)} /></IconContext.Provider>
        }
    }

    const handleBestekFilterParamsChange = (params?: BestekFilterParams) => {
        if (params) {
            const queryParams = mapFilterParamsToQueryParams(params);
            setBestekFilterParamsQuery(queryParams);
            setBestekFilterParams(params);
        }

        fetchWithTimeout(500);
    }

    const fetchWithTimeout = (timeout: number = 1000): void => {
        setLoading(true);

        if (fetchTimeout) {
            clearTimeout(fetchTimeout);
            setFetchTimeout(null);
        }

        setFetchTimeout(setTimeout(() => setDoFetch(true), timeout));
    }

    return (
        <>
            {/* Filter */}
            <Container fluid className="pt-4">
                <Row>
                    <Col sm={12} md={4}>
                        <label htmlFor="min-length">Minimale lengte</label>
                        <Form.Control value={minLength} type="number" min={1} step={1} onChange={(e) => setMinLength(parseInt(e.target.value))} />
                    </Col>
                    {/* <Col sm={12} md={4}>
                        <label htmlFor="min-length">Maximale lengte</label>
                        <Form.Control value={maxLength} type="number" min={1} step={1} onChange={(e) => setMaxLength(parseInt(e.target.value))} />
                    </Col> */}
                    <Col sm={12} md={4}>
                        <label htmlFor="min-length">Minimale telling</label>
                        <Form.Control value={minCount} type="number" min={1} step={1} onChange={(e) => setMinCount(parseInt(e.target.value))} />
                    </Col>
                    <Col sm={12} md={4}>
                        <label htmlFor="min-length">Minimaal aantal bestekken</label>
                        <Form.Control value={minBestekCount} type="number" min={1} step={1} onChange={(e) => setMinBestekCount(parseInt(e.target.value))} />
                    </Col>
                </Row>
                <Row className="mt-4">
                    <Col>
                        <HeaderedContainer title="Bestek filter" customRightComponent={bestekFilterCollapseIcon()}>
                            {
                                bestekFilterParamsExpanded && (
                                    <div className="mt-3">
                                        <BestekFilter stateContext="local" initialLocalParams={initialBestekFilterParams} onFilterButtonClick={p => handleBestekFilterParamsChange(p)} disableHoofdCodeFiltering="partial" />
                                    </div>
                                )
                            }
                        </HeaderedContainer>
                    </Col>
                </Row>

                {/* Show results for length */}
                { result.length > 0 && !loading && ( 
                    <Row className="mt-4">
                        <Col>
                            Toon resultaten van lengte: 
                            <ButtonGroup className="ms-4">
                                {
                                    Array.from({length: longestSubsentenceLength}, (_, i) => i + 1).reverse().map(n => {
                                        if (n < minLength) { return <></> }

                                        return <Button disabled={!availableLengths.includes(n)} variant={n === showResultForLength ? 'primary' : (availableLengths.includes(n) ? 'secondary' : 'outline-secondary')} onClick={() => setShowResultForLength(n)}>{n}</Button>
                                    })
                                }
                            </ButtonGroup>
                        </Col>
                    </Row>
                )}
            </Container>

            {
                shouldAddMoreFilterCriteria && (
                    <Row><Col><div className={styles.minimum_filter_not_reached}>{ Object.keys(bestekFilterParamsQuery).length < 1 ? 'Selecteer ten minste 1 filtercriterium om resultaten te tonen.' : 'Voeg meer filtercriteria toe om resultaten te tonen.' }</div></Col></Row>
                )
            }

            {/* List */}
            {
                !shouldAddMoreFilterCriteria && (
                    <Table className="mt-4" size="sm" striped hover>
                        <thead>
                            <tr>
                                <th style={{width: '10%'}}>#</th>
                                <th>Aantal bestekken</th>
                                <th>Subzin</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                loading && (
                                    <tr>
                                        <td colSpan={3} style={{textAlign: 'center'}}>
                                            <Spinner size="sm" style={{marginRight: '5px'}}></Spinner> Resultaten ophalen...
                                        </td>
                                    </tr>
                                )
                            }

                            {
                                !loading && filteredResultSet.length === 0 && (
                                    <tr>
                                        <td colSpan={3} style={{textAlign: 'center'}}>
                                            Geen resultaten voor de opgegeven zoekfilters.
                                        </td>
                                    </tr>
                                )
                            }

                            {
                                !loading && filteredResultSet.length > 0 && (
                                    <>
                                        {
                                            shownResultSet.map((r) => {
                                                return (
                                                    <tr>
                                                        <td>{ r.count }</td>
                                                        <td>{ r.bestek_ids.length }</td>
                                                        <td>{ r.word }</td>
                                                    </tr>
                                                )
                                            })
                                        }
                                    </>
                                )
                            }
                        </tbody>
                    </Table>
                )
            }

            {/* Table pagination */}
            <PaginationControl 
                page={activePage}
                between={4}
                total={filteredResultSet.length}
                limit={pageSize}
                changePage={setActivePage}
                ellipsis={1}
            />
        </>
    )
}

export default connect(mapStateToProps)(WordCountList);